import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../providers/AmplifyAuthProvider'
import { usePageHeader } from '../../../providers/PageHeaderProvider'
import {
  Container,
  Header,
  Alert,
  Box,
  Spinner,
  Button
} from '@cloudscape-design/components'
import { Id } from '../../../../convex/_generated/dataModel'
import { DashboardBuilder } from '../components/DashboardBuilder'
import { Dashboard } from '../types'

export const DashboardBuilderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { sessionId } = useAmplifyAuth()
  
  // Check if we're editing an existing dashboard
  const isEditing = Boolean(id)
  
  // Fetch existing dashboard if editing
  const dashboard = useQuery(
    id && sessionId ? api.components.dashboards.index.getDashboard : "skip",
    id && sessionId ? { sessionId, dashboardId: id as Id<"dashboards"> } : "skip"
  )
  
  // Mutations for saving dashboards
  const createDashboard = useMutation(api.components.dashboards.index.createDashboard)
  const updateDashboard = useMutation(api.components.dashboards.index.updateDashboard)

  const handleSave = async (dashboardData: Dashboard) => {
    if (!sessionId) {
      throw new Error('No session available')
    }

    try {
      if (isEditing && id) {
        // Update existing dashboard
        await updateDashboard({
          sessionId,
          dashboardId: id as Id<"dashboards">,
          updates: {
            name: dashboardData.name,
            description: dashboardData.description,
            layout: dashboardData.layout,
            widgets: dashboardData.widgets,
            default_time_range: dashboardData.default_time_range,
            refresh_interval: dashboardData.refresh_interval,
            is_public: dashboardData.is_public
          }
        })
      } else {
        // Create new dashboard
        const newDashboardId = await createDashboard({
          sessionId,
          dashboard: {
            name: dashboardData.name,
            description: dashboardData.description,
            layout: dashboardData.layout,
            widgets: dashboardData.widgets,
            default_time_range: dashboardData.default_time_range,
            refresh_interval: dashboardData.refresh_interval,
            is_public: dashboardData.is_public
          }
        })
        
        // Navigate to the new dashboard
        navigate(`/dashboards/${newDashboardId}`)
        return
      }
      
      // Navigate back to dashboard viewer
      navigate(`/dashboards/${id}`)
    } catch (error: any) {
      throw new Error(`Failed to save dashboard: ${error.message}`)
    }
  }

  const handleCancel = () => {
    if (isEditing) {
      navigate(`/dashboards/${id}`)
    } else {
      navigate('/dashboards')
    }
  }

  // Show loading state
  if (isEditing && dashboard === undefined) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <div>Loading dashboard...</div>
        </Box>
      </Container>
    )
  }

  // Show error state
  if (isEditing && dashboard === null) {
    return (
      <Container header={<Header variant="h1">Dashboard Not Found</Header>}>
        <Alert
          type="error"
          statusIconAriaLabel="Error"
          header="Dashboard could not be loaded"
          action={
            <Button onClick={() => navigate('/dashboards')}>
              Back to Dashboards
            </Button>
          }
        >
          The dashboard you're trying to edit could not be found. It may have been deleted or you may not have permission to access it.
        </Alert>
      </Container>
    )
  }

  return (
    <DashboardBuilder
      dashboard={dashboard || undefined}
      isEditing={isEditing}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  )
}